import React, { useState, useEffect, useCallback } from 'react';

const API = 'https://api.botanix.in/api/sessions';
const MENU_API = 'https://api.botanix.in/api/kitchen/menu';

const BOTANIX_PRIMARY = '#153D4B';
const BOTANIX_GOLD = '#C9A84C';
const BOTANIX_BG = '#F2F2EC';

const C = {
  accent:'#3AAF58', accentLight:'#EAF7EE',
  bg:'#F3F6F3', surface:'#FFFFFF',
  border:'#E2E8E2', border2:'#CDD8CD',
  text:'#1A2E1E', text2:'#3D5E44', text3:'#7A9A80',
  amber:'#D4840F', amberBg:'#FEF6E7', amberBorder:'#F0D499',
  red:'#C83333', redBg:'#FDEAEA',
  blue:'#2563A8', blueBg:'#EAF1FB',
  primary: BOTANIX_PRIMARY, gold: BOTANIX_GOLD,
};

const inr = n => '₹' + Number(Math.round(n)||0).toLocaleString('en-IN');

const FUNCTIONS = [
  { id:'mehendi',   label:'Mehendi',          icon:'🌿', color:'#7CB342' },
  { id:'haldi',     label:'Haldi',             icon:'💛', color:'#F9A825' },
  { id:'sangeet',   label:'Sangeet',           icon:'🎵', color:'#8E24AA' },
  { id:'cocktail',  label:'Cocktail',          icon:'🍸', color:'#E64A19' },
  { id:'wedding',   label:'Wedding Ceremony',  icon:'💍', color:'#C9A84C' },
  { id:'reception', label:'Reception',         icon:'🎊', color:'#153D4B' },
  { id:'brunch',    label:'Brunch',            icon:'🥂', color:'#00897B' },
  { id:'pooja',     label:'Pooja / Rituals',   icon:'🪔', color:'#E65100' },
];

// Group cuisines for display
const CUISINE_GROUPS = [
  { group:'Indian', cuisines:['Indian','Indian - North Indian','Indian - South Indian','Indian - Punjabi','Indian - Mughlai','Indian - Rajasthani','Indian - Hyderabadi','Indian - Kashmiri','Indian - Goan','Indian - Coastal','Indian - Bengali','Indian - Street Food','Indian - Tandoor','Indian - Homestyle','Indian - Mumbai','Indian - Central Indian'] },
  { group:'Chinese', cuisines:['Chinese','Chinese - Sichuan'] },
  { group:'Continental', cuisines:['Continental','European','British','French Bakery','American'] },
  { group:'Café & Bakery', cuisines:['Café','Bakery','American Bakery','Artisan Bakery','French Bakery','Healthy Café'] },
];

const ADDON_SUGGESTIONS = ['DJ & Sound System','Live Band','Floral Decoration','Stage & Backdrop Setup','Photography & Videography','Drone Coverage','Fireworks','Horse / Baggi Arrangement','Mehendi Artist','Tent & Furniture','LED Wall','Projector & Screen','Photo Booth','Cocktail Counter','Welcome Drinks Counter','Other'];

const TERMS_DEFAULT = `1. 50% advance payment required to confirm the booking.
2. This proposal is valid for 30 days from the date of issue.
3. All prices are inclusive of GST as mentioned.
4. Any additional requirements will be charged extra.
5. Cancellation charges apply as per resort policy.
6. Food items are subject to availability and seasonal changes.
7. Resort management reserves the right to make changes in case of force majeure.
8. The resort will not be responsible for any loss or damage to personal belongings.`;

const inp = { width:'100%', padding:'9px 12px', background:'#F3F6F3', border:'1.5px solid #CDD8CD', borderRadius:8, color:'#1A2E1E', fontSize:13, boxSizing:'border-box' };
const lbl = t => <div style={{fontSize:11,fontWeight:700,color:'#7A9A80',textTransform:'uppercase',letterSpacing:'.05em',marginBottom:5}}>{t}</div>;

// ── PROPOSAL PRINT VIEW ──────────────────────────────────────────────────────
function ProposalPrintView({ proposal }) {
  const fns = proposal.functions || [];
  const totalAmount = parseFloat(proposal.total_amount||0);
  const discountAmt = parseFloat(proposal.discount_amount||0);
  const gstAmt = parseFloat(proposal.gst_amount||0);
  const netAmount = totalAmount - discountAmt + gstAmt;
  const validUntil = proposal.valid_until ? new Date(proposal.valid_until).toLocaleDateString('en-IN',{day:'numeric',month:'long',year:'numeric'}) : '';

  const fnTotal = fn => (fn.items||[]).reduce((s,i)=>s+parseFloat(i.amount||0),0);

  return (
    <div id="proposal-print" style={{fontFamily:'Georgia,serif',maxWidth:800,margin:'0 auto',background:'#fff',color:'#1A2E1E'}}>

      {/* COVER PAGE */}
      <div style={{background:`linear-gradient(135deg,${BOTANIX_PRIMARY} 0%,#1E5A70 60%,#0D2830 100%)`,minHeight:420,display:'flex',flexDirection:'column',alignItems:'center',justifyContent:'center',padding:'60px 40px',textAlign:'center',position:'relative',overflow:'hidden'}}>
        <div style={{position:'absolute',inset:0,opacity:.04,backgroundImage:'radial-gradient(circle at 30% 50%,white 1px,transparent 1px)',backgroundSize:'28px 28px'}} />
        <div style={{position:'absolute',top:0,left:0,right:0,height:4,background:`linear-gradient(90deg,${BOTANIX_GOLD},#E8C97A,${BOTANIX_GOLD})`}} />
        <img src="https://botanix.in/wp-content/uploads/2025/12/Botanix-Nature-Resort-Logo.webp" alt="Botanix" style={{height:70,marginBottom:24,filter:'brightness(0) invert(1)'}} onError={e=>e.target.style.display='none'} />
        <div style={{color:'rgba(255,255,255,.6)',fontSize:11,letterSpacing:'.4em',textTransform:'uppercase',marginBottom:12}}>Wedding & Events — Exclusive Proposal</div>
        <div style={{color:'#fff',fontSize:38,fontWeight:700,marginBottom:8,fontStyle:'italic',textShadow:'0 2px 8px rgba(0,0,0,.3)'}}>Celebrate in Style</div>
        <div style={{color:BOTANIX_GOLD,fontSize:24,fontWeight:700,marginBottom:24,letterSpacing:'.02em'}}>For {proposal.client_name}</div>
        <div style={{color:'rgba(255,255,255,.75)',fontSize:13,lineHeight:2}}>
          {proposal.event_date && <div>📅 {new Date(proposal.event_date).toLocaleDateString('en-IN',{day:'numeric',month:'long',year:'numeric'})}{proposal.event_end_date&&proposal.event_end_date!==proposal.event_date?` — ${new Date(proposal.event_end_date).toLocaleDateString('en-IN',{day:'numeric',month:'long',year:'numeric'})}`:''}</div>}
          {proposal.pax_count && <div>👥 {proposal.pax_count} Guests</div>}
          {proposal.venue && <div>📍 {proposal.venue}</div>}
        </div>
        <div style={{marginTop:28,padding:'8px 24px',border:`1px solid ${BOTANIX_GOLD}`,borderRadius:99,color:BOTANIX_GOLD,fontSize:11,letterSpacing:'.1em'}}>Valid Until: {validUntil}</div>
        <div style={{position:'absolute',bottom:20,fontSize:10,color:'rgba(255,255,255,.35)'}}>#{proposal.proposal_number} · Prepared by: {proposal.salesperson||'Botanix Sales Team'}</div>
        <div style={{position:'absolute',bottom:0,left:0,right:0,height:4,background:`linear-gradient(90deg,${BOTANIX_GOLD},#E8C97A,${BOTANIX_GOLD})`}} />
      </div>

      {/* ABOUT BOTANIX */}
      <div style={{padding:'44px 48px 32px',background:'#fff'}}>
        <div style={{display:'flex',alignItems:'center',gap:12,marginBottom:20}}>
          <div style={{width:4,height:32,background:BOTANIX_PRIMARY,borderRadius:2}} />
          <div style={{fontSize:22,fontWeight:700,color:BOTANIX_PRIMARY}}>About Botanix Nature Resort</div>
        </div>
        <div style={{fontSize:13,lineHeight:2,color:'#444',marginBottom:24}}>
          Nestled amidst the majestic Aravallis on Sohna Road, Gurgaon, <strong>Botanix Nature Resort</strong> is an enchanting destination that transforms your celebrations into lifelong memories. Our sprawling property seamlessly blends the serenity of nature with world-class luxury, offering an unmatched backdrop for weddings and grand events.
        </div>
        <div style={{display:'grid',gridTemplateColumns:'repeat(4,1fr)',gap:14}}>
          {[['🌿','Natural Setting','Lush Aravalli landscapes'],['🏛️','Multiple Venues','Lawns, halls & amphitheatre'],['👨‍🍳','Expert Chefs','Multi-cuisine culinary team'],['🎪','Event Experts','Dedicated coordination team']].map(([icon,title,desc])=>(
            <div key={title} style={{textAlign:'center',padding:'18px 12px',background:BOTANIX_BG,borderRadius:10,border:`1px solid #E8E4DC`}}>
              <div style={{fontSize:26,marginBottom:8}}>{icon}</div>
              <div style={{fontSize:12,fontWeight:700,color:BOTANIX_PRIMARY,marginBottom:4}}>{title}</div>
              <div style={{fontSize:10,color:'#888',lineHeight:1.5}}>{desc}</div>
            </div>
          ))}
        </div>
      </div>

      {/* EVENT OVERVIEW */}
      <div style={{padding:'28px 48px',background:BOTANIX_BG,borderTop:`3px solid #E8E4DC`,borderBottom:`3px solid #E8E4DC`}}>
        <div style={{display:'flex',alignItems:'center',gap:12,marginBottom:20}}>
          <div style={{width:4,height:28,background:BOTANIX_GOLD,borderRadius:2}} />
          <div style={{fontSize:20,fontWeight:700,color:BOTANIX_PRIMARY}}>Event Overview</div>
        </div>
        <div style={{display:'grid',gridTemplateColumns:'1fr 1fr',gap:10}}>
          {[
            ['Client / Family',proposal.client_name],
            ['Contact',proposal.client_phone||'—'],
            ['Email',proposal.client_email||'—'],
            ['Event Date',proposal.event_date?new Date(proposal.event_date).toLocaleDateString('en-IN',{weekday:'long',day:'numeric',month:'long',year:'numeric'}):'—'],
            ['Event Duration',proposal.event_end_date&&proposal.event_end_date!==proposal.event_date?`${new Date(proposal.event_date).toLocaleDateString('en-IN')} to ${new Date(proposal.event_end_date).toLocaleDateString('en-IN')}`:'1 Day'],
            ['Expected Guests',`${proposal.pax_count} Pax`],
            ['Primary Venue',proposal.venue||'—'],
            ['Functions Planned',`${fns.length} Function(s)`],
            ['Salesperson',proposal.salesperson||'—'],
            ['Proposal #',proposal.proposal_number],
          ].map(([k,v])=>(
            <div key={k} style={{display:'flex',gap:10,padding:'9px 14px',background:'#fff',borderRadius:8,border:'1px solid #E8E4DC'}}>
              <span style={{fontSize:11,color:'#888',minWidth:110,flexShrink:0}}>{k}:</span>
              <span style={{fontSize:12,fontWeight:700,color:BOTANIX_PRIMARY}}>{v}</span>
            </div>
          ))}
        </div>
      </div>

      {/* FUNCTIONS */}
      {fns.map((fn,fi)=>{
        const fnDef = FUNCTIONS.find(f=>f.id===fn.function_name)||{label:fn.function_name,color:BOTANIX_PRIMARY,icon:'🎉'};
        const foodItems = (fn.items||[]).filter(i=>i.item_type==='food');
        const pkgItems = (fn.items||[]).filter(i=>i.item_type==='package');
        const addonItems = (fn.items||[]).filter(i=>i.item_type==='addon');
        const fTotal = fnTotal(fn);
        const allFood = [...foodItems, ...pkgItems];

        return (
          <div key={fn.id} style={{padding:'32px 48px',borderBottom:`4px solid ${BOTANIX_BG}`}}>
            {/* Function Header */}
            <div style={{display:'flex',justifyContent:'space-between',alignItems:'flex-start',marginBottom:24}}>
              <div style={{display:'flex',alignItems:'center',gap:14}}>
                <div style={{width:48,height:48,borderRadius:12,background:fnDef.color+'22',display:'flex',alignItems:'center',justifyContent:'center',fontSize:24,border:`2px solid ${fnDef.color}33`}}>
                  {fnDef.icon}
                </div>
                <div>
                  <div style={{fontSize:22,fontWeight:800,color:BOTANIX_PRIMARY}}>{fnDef.label}</div>
                  <div style={{fontSize:12,color:'#777',marginTop:3,display:'flex',gap:12}}>
                    {fn.function_date&&<span>📅 {new Date(fn.function_date).toLocaleDateString('en-IN',{weekday:'long',day:'numeric',month:'long'})}</span>}
                    {fn.function_time&&<span>🕐 {fn.function_time}</span>}
                    {fn.venue&&<span>📍 {fn.venue}</span>}
                    {fn.pax_count&&<span>👥 {fn.pax_count} Pax</span>}
                  </div>
                </div>
              </div>
              {fTotal>0&&<div style={{textAlign:'right'}}>
                <div style={{fontSize:22,fontWeight:900,color:fnDef.color}}>{inr(fTotal)}</div>
                <div style={{fontSize:10,color:'#999'}}>Function Total</div>
              </div>}
            </div>

            {/* Food Packages / Cuisine packages */}
            {pkgItems.length>0&&(
              <div style={{marginBottom:20}}>
                <div style={{fontSize:12,fontWeight:800,color:BOTANIX_PRIMARY,textTransform:'uppercase',letterSpacing:'.08em',marginBottom:12,display:'flex',alignItems:'center',gap:8}}>
                  <div style={{width:20,height:2,background:BOTANIX_GOLD}} />
                  Food & Beverage Packages
                  <div style={{flex:1,height:1,background:'#eee'}} />
                </div>
                {pkgItems.map((item,i)=>(
                  <div key={i} style={{marginBottom:10,padding:'14px 18px',background:BOTANIX_BG,borderRadius:10,border:'1px solid #E8E4DC'}}>
                    <div style={{display:'flex',justifyContent:'space-between',alignItems:'flex-start',marginBottom:6}}>
                      <div>
                        <div style={{fontSize:14,fontWeight:700,color:BOTANIX_PRIMARY}}>{item.item_name}</div>
                        <div style={{fontSize:11,color:'#888',marginTop:2}}>{item.description}</div>
                      </div>
                      <div style={{textAlign:'right',flexShrink:0,marginLeft:16}}>
                        <div style={{fontSize:13,fontWeight:700,color:fnDef.color}}>{item.is_complimentary?'Complimentary':inr(item.amount)}</div>
                        {!item.is_complimentary&&<div style={{fontSize:10,color:'#999'}}>{inr(item.rate)}/pax × {item.quantity} pax</div>}
                      </div>
                    </div>
                    {/* Show cuisine items included */}
                    {item.cuisine_items&&item.cuisine_items.length>0&&(
                      <div style={{marginTop:8}}>
                        <div style={{fontSize:10,color:'#888',marginBottom:4,fontWeight:700,textTransform:'uppercase'}}>Includes:</div>
                        <div style={{display:'flex',flexWrap:'wrap',gap:4}}>
                          {item.cuisine_items.slice(0,20).map((ci,j)=>(
                            <span key={j} style={{padding:'2px 8px',background:'#fff',borderRadius:99,fontSize:10,color:BOTANIX_PRIMARY,border:'1px solid #ddd',display:'flex',alignItems:'center',gap:3}}>
                              <span style={{width:6,height:6,borderRadius:'50%',background:ci.is_veg?C.accent:C.red,display:'inline-block'}} />
                              {ci.item_name}
                            </span>
                          ))}
                          {item.cuisine_items.length>20&&<span style={{fontSize:10,color:'#999'}}>+{item.cuisine_items.length-20} more items</span>}
                        </div>
                      </div>
                    )}
                  </div>
                ))}
              </div>
            )}

            {/* Individual food items */}
            {foodItems.length>0&&(
              <div style={{marginBottom:20}}>
                <div style={{fontSize:12,fontWeight:800,color:BOTANIX_PRIMARY,textTransform:'uppercase',letterSpacing:'.08em',marginBottom:12,display:'flex',alignItems:'center',gap:8}}>
                  <div style={{width:20,height:2,background:BOTANIX_GOLD}} />
                  Additional Food Items
                  <div style={{flex:1,height:1,background:'#eee'}} />
                </div>
                <table style={{width:'100%',borderCollapse:'collapse',fontSize:12}}>
                  <thead>
                    <tr style={{background:BOTANIX_PRIMARY,color:'#fff'}}>
                      {['Item','Type','Qty','Rate/Pax','Amount'].map(h=>(
                        <th key={h} style={{padding:'8px 12px',textAlign:'left',fontSize:11,fontWeight:600}}>{h}</th>
                      ))}
                    </tr>
                  </thead>
                  <tbody>
                    {foodItems.map((item,i)=>(
                      <tr key={i} style={{borderBottom:'1px solid #eee',background:i%2===0?'#fff':BOTANIX_BG}}>
                        <td style={{padding:'8px 12px',fontWeight:600,color:BOTANIX_PRIMARY}}>
                          <span style={{width:7,height:7,borderRadius:'50%',background:item.is_veg?C.accent:C.red,display:'inline-block',marginRight:5}} />
                          {item.item_name}
                        </td>
                        <td style={{padding:'8px 12px',color:'#777',fontSize:11}}>{item.description||'—'}</td>
                        <td style={{padding:'8px 12px',color:'#555'}}>{item.quantity} pax</td>
                        <td style={{padding:'8px 12px',color:'#555'}}>{item.is_complimentary?'Free':inr(item.rate)}</td>
                        <td style={{padding:'8px 12px',fontWeight:700,color:item.is_complimentary?C.accent:BOTANIX_PRIMARY}}>{item.is_complimentary?'Included':inr(item.amount)}</td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            )}

            {/* Add-ons */}
            {addonItems.length>0&&(
              <div>
                <div style={{fontSize:12,fontWeight:800,color:BOTANIX_PRIMARY,textTransform:'uppercase',letterSpacing:'.08em',marginBottom:12,display:'flex',alignItems:'center',gap:8}}>
                  <div style={{width:20,height:2,background:fnDef.color}} />
                  Services & Add-ons
                  <div style={{flex:1,height:1,background:'#eee'}} />
                </div>
                <div style={{display:'grid',gridTemplateColumns:'1fr 1fr',gap:8}}>
                  {addonItems.map((item,i)=>(
                    <div key={i} style={{display:'flex',justifyContent:'space-between',alignItems:'center',padding:'10px 14px',background:BOTANIX_BG,borderRadius:8,border:'1px solid #E8E4DC'}}>
                      <div>
                        <div style={{fontSize:12,fontWeight:700,color:BOTANIX_PRIMARY}}>{item.item_name}</div>
                        {item.description&&<div style={{fontSize:10,color:'#888',marginTop:2}}>{item.description}</div>}
                      </div>
                      <div style={{fontWeight:800,color:item.is_complimentary?C.accent:fnDef.color,fontSize:13,flexShrink:0,marginLeft:10}}>
                        {item.is_complimentary?'Complimentary':inr(item.amount)}
                      </div>
                    </div>
                  ))}
                </div>
              </div>
            )}
          </div>
        );
      })}

      {/* PRICING SUMMARY */}
      <div style={{padding:'32px 48px',background:'#fff',borderBottom:`4px solid ${BOTANIX_BG}`}}>
        <div style={{display:'flex',alignItems:'center',gap:12,marginBottom:20}}>
          <div style={{width:4,height:28,background:BOTANIX_PRIMARY,borderRadius:2}} />
          <div style={{fontSize:20,fontWeight:700,color:BOTANIX_PRIMARY}}>Pricing Summary</div>
        </div>
        <div style={{display:'grid',gridTemplateColumns:'1fr 1fr',gap:24}}>
          {/* Per function breakdown */}
          <div>
            <div style={{fontSize:11,fontWeight:700,color:'#888',textTransform:'uppercase',marginBottom:10}}>Per Function Breakdown</div>
            {fns.map(fn=>{
              const fTotal = fnTotal(fn);
              const fnDef = FUNCTIONS.find(f=>f.id===fn.function_name)||{label:fn.function_name,color:BOTANIX_PRIMARY};
              return fTotal>0?(
                <div key={fn.id} style={{display:'flex',justifyContent:'space-between',alignItems:'center',padding:'9px 14px',background:BOTANIX_BG,borderRadius:8,marginBottom:6,border:'1px solid #E8E4DC'}}>
                  <span style={{fontSize:13,color:BOTANIX_PRIMARY,fontWeight:600}}>{fnDef.label}</span>
                  <span style={{fontSize:14,fontWeight:800,color:fnDef.color||BOTANIX_PRIMARY}}>{inr(fTotal)}</span>
                </div>
              ):null;
            })}
          </div>
          {/* Total */}
          <div>
            <div style={{fontSize:11,fontWeight:700,color:'#888',textTransform:'uppercase',marginBottom:10}}>Payment Summary</div>
            <div style={{border:'1px solid #E8E4DC',borderRadius:10,overflow:'hidden'}}>
              <div style={{display:'flex',justifyContent:'space-between',padding:'10px 16px',borderBottom:'1px solid #eee',fontSize:13}}>
                <span style={{color:'#666'}}>Subtotal</span>
                <span style={{fontWeight:700}}>{inr(totalAmount)}</span>
              </div>
              {discountAmt>0&&(
                <div style={{display:'flex',justifyContent:'space-between',padding:'10px 16px',borderBottom:'1px solid #eee',fontSize:13,color:C.accent}}>
                  <span>Discount ({proposal.discount_pct}%)</span>
                  <span>-{inr(discountAmt)}</span>
                </div>
              )}
              {gstAmt>0&&(
                <div style={{display:'flex',justifyContent:'space-between',padding:'10px 16px',borderBottom:'1px solid #eee',fontSize:13,color:'#666'}}>
                  <span>GST</span>
                  <span>{inr(gstAmt)}</span>
                </div>
              )}
              <div style={{display:'flex',justifyContent:'space-between',padding:'14px 16px',background:BOTANIX_PRIMARY,color:'#fff'}}>
                <span style={{fontSize:15,fontWeight:700}}>Total Amount</span>
                <span style={{fontSize:20,fontWeight:900}}>{inr(netAmount)}</span>
              </div>
              {parseFloat(proposal.advance_required||0)>0&&(
                <div style={{display:'flex',justifyContent:'space-between',padding:'10px 16px',background:BOTANIX_BG,fontSize:13}}>
                  <span style={{color:'#666'}}>Advance to Confirm Booking</span>
                  <span style={{fontWeight:800,color:BOTANIX_PRIMARY}}>{inr(proposal.advance_required)}</span>
                </div>
              )}
            </div>
          </div>
        </div>
      </div>

      {/* TERMS */}
      <div style={{padding:'32px 48px',background:BOTANIX_BG}}>
        <div style={{display:'flex',alignItems:'center',gap:12,marginBottom:16}}>
          <div style={{width:4,height:24,background:BOTANIX_GOLD,borderRadius:2}} />
          <div style={{fontSize:18,fontWeight:700,color:BOTANIX_PRIMARY}}>Terms & Conditions</div>
        </div>
        <div style={{fontSize:11,color:'#666',lineHeight:2.2,columns:2,columnGap:32}}>
          {(proposal.terms||TERMS_DEFAULT).split('\n').filter(l=>l.trim()).map((line,i)=>(
            <div key={i} style={{breakInside:'avoid'}}>{line}</div>
          ))}
        </div>
        <div style={{marginTop:32,display:'grid',gridTemplateColumns:'1fr 1fr',gap:40}}>
          <div style={{textAlign:'center'}}>
            <div style={{height:50,borderBottom:'1px solid #ccc',marginBottom:8}} />
            <div style={{fontSize:11,color:'#999'}}>Client / Authorised Representative</div>
            <div style={{fontSize:10,color:'#bbb',marginTop:2}}>{proposal.client_name}</div>
          </div>
          <div style={{textAlign:'center'}}>
            <div style={{height:50,borderBottom:'1px solid #ccc',marginBottom:8}} />
            <div style={{fontSize:11,color:'#999'}}>For Botanix Nature Resort</div>
            <div style={{fontSize:10,color:'#bbb',marginTop:2}}>Authorised Signatory</div>
          </div>
        </div>
        <div style={{marginTop:28,textAlign:'center',fontSize:10,color:'#bbb',borderTop:'1px solid #ddd',paddingTop:16}}>
          Botanix Nature Resort · Sohna Road, Gurgaon, Haryana · reservations@botanix.in<br/>
          This is a computer-generated proposal. All prices are subject to change without prior notice.
        </div>
      </div>
    </div>
  );
}

// ── FUNCTION EDITOR ──────────────────────────────────────────────────────────
function FunctionEditor({ fn, menuItems, onSave, onClose }) {
  const [name, setName] = useState(fn?.function_name||'');
  const [date, setDate] = useState(fn?.function_date?fn.function_date.slice(0,10):'');
  const [time, setTime] = useState(fn?.function_time||'');
  const [venue, setVenue] = useState(fn?.venue||'');
  const [pax, setPax] = useState(fn?.pax_count||100);
  const [items, setItems] = useState(fn?.items||[]);
  const [activeTab, setActiveTab] = useState('cuisine');

  // Cuisine package selection
  const [selectedCuisines, setSelectedCuisines] = useState([]);
  const [selectedDishes, setSelectedDishes] = useState([]);
  const [pkgPricePerPax, setPkgPricePerPax] = useState(1500);
  const [pkgName, setPkgName] = useState('');
  const [pkgMealType, setPkgMealType] = useState('Dinner');

  // Addons
  const [addonName, setAddonName] = useState('');
  const [addonDesc, setAddonDesc] = useState('');
  const [addonAmt, setAddonAmt] = useState('');
  const [addonFree, setAddonFree] = useState(false);

  // Get cuisines available in menu
  const availableCuisines = [...new Set(menuItems.map(m=>m.cuisine).filter(Boolean))].sort();

  // Get items for selected cuisines
  const cuisineMenuItems = menuItems.filter(m=>selectedCuisines.includes(m.cuisine));

  const toggleCuisine = (cuisine) => {
    setSelectedCuisines(p=>p.includes(cuisine)?p.filter(c=>c!==cuisine):[...p,cuisine]);
  };

  const addCuisinePackage = () => {
    if (!selectedCuisines.length) return;
    const name = pkgName || `${pkgMealType} Package — ${selectedCuisines.join(', ')}`;
    const amt = parseFloat(pkgPricePerPax||0)*parseFloat(pax||0);
    const newPkg = {
      item_type:'package',
      item_name: name,
      description: `${pkgMealType} · ${selectedCuisines.join(', ')} · ${pax} pax`,
      quantity: pax,
      unit: 'pax',
      rate: parseFloat(pkgPricePerPax||0),
      amount: amt,
      is_complimentary: false,
      sort_order: items.length,
      cuisine_items: cuisineMenuItems,
    };
    setItems(p=>[...p.filter(i=>i.item_type!=='package'||i.item_name!==name), newPkg]);
    setSelectedCuisines([]);
    setPkgName('');
  };

  const addAddon = () => {
    if (!addonName) return;
    setItems(p=>[...p, {
      item_type:'addon', item_name:addonName, description:addonDesc,
      quantity:1, unit:'event', rate:parseFloat(addonAmt||0),
      amount:parseFloat(addonAmt||0), is_complimentary:addonFree, sort_order:p.length
    }]);
    setAddonName(''); setAddonDesc(''); setAddonAmt(''); setAddonFree(false);
  };

  const removeItem = idx => setItems(p=>p.filter((_,i)=>i!==idx));
  const updateItemRate = (idx, rate) => {
    setItems(p=>p.map((item,i)=>i===idx?{...item,rate:parseFloat(rate||0),amount:parseFloat(rate||0)*parseFloat(item.quantity||1)}:item));
  };

  const fnTotal = items.reduce((s,i)=>s+parseFloat(i.is_complimentary?0:i.amount||0),0);

  // Group available cuisines
  const cuisineByGroup = CUISINE_GROUPS.map(g=>({
    ...g, available: g.cuisines.filter(c=>availableCuisines.includes(c))
  })).filter(g=>g.available.length>0);

  // Also add any cuisines not in groups
  const groupedCuisines = CUISINE_GROUPS.flatMap(g=>g.cuisines);
  const otherCuisines = availableCuisines.filter(c=>!groupedCuisines.includes(c));

  return (
    <div style={{position:'fixed',inset:0,background:'rgba(0,0,0,.6)',zIndex:300,display:'flex',alignItems:'center',justifyContent:'center',padding:16,overflowY:'auto'}}>
      <div style={{background:C.surface,borderRadius:16,width:'100%',maxWidth:780,maxHeight:'92vh',overflowY:'auto',padding:28}}>
        <div style={{display:'flex',justifyContent:'space-between',alignItems:'center',marginBottom:20}}>
          <div style={{fontSize:17,fontWeight:800,color:C.text}}>{fn?.id?'Edit Function':'Add Function'}</div>
          <button onClick={onClose} style={{background:C.bg,border:`1px solid ${C.border}`,borderRadius:8,padding:'6px 14px',cursor:'pointer',color:C.text2}}>Cancel</button>
        </div>

        {/* Function Type */}
        {!fn?.id&&(
          <div style={{marginBottom:16}}>
            {lbl('Select Function Type')}
            <div style={{display:'flex',gap:8,flexWrap:'wrap'}}>
              {FUNCTIONS.map(f=>(
                <button key={f.id} onClick={()=>setName(f.id)}
                  style={{padding:'7px 14px',border:`2px solid ${name===f.id?f.color:C.border}`,borderRadius:8,background:name===f.id?f.color+'22':'transparent',color:name===f.id?f.color:C.text2,fontSize:13,fontWeight:600,cursor:'pointer',transition:'all .2s'}}>
                  {f.icon} {f.label}
                </button>
              ))}
            </div>
          </div>
        )}

        {/* Function Details */}
        <div style={{display:'grid',gridTemplateColumns:'1fr 1fr 1fr 1fr',gap:10,marginBottom:16}}>
          <div>{lbl('Date')}<input style={inp} type="date" value={date} onChange={e=>setDate(e.target.value)} /></div>
          <div>{lbl('Time')}<input style={inp} value={time} onChange={e=>setTime(e.target.value)} placeholder="7:00 PM" /></div>
          <div>{lbl('Venue')}<input style={inp} value={venue} onChange={e=>setVenue(e.target.value)} placeholder="Central Lawn" /></div>
          <div>{lbl('Pax Count')}<input style={inp} type="number" value={pax} onChange={e=>setPax(parseInt(e.target.value)||100)} /></div>
        </div>

        {/* Tabs */}
        <div style={{display:'flex',gap:6,marginBottom:16,borderBottom:`2px solid ${C.border}`,paddingBottom:0}}>
          {[['cuisine','🍽️ Food Packages'],['addons','✨ Add-ons'],['summary',`📋 Summary (${items.length})`]].map(([id,label])=>(
            <button key={id} onClick={()=>setActiveTab(id)}
              style={{padding:'8px 16px',background:'transparent',border:'none',borderBottom:activeTab===id?`3px solid ${C.accent}`:'3px solid transparent',color:activeTab===id?C.accent:C.text2,fontSize:13,fontWeight:600,cursor:'pointer',marginBottom:-2}}>
              {label}
            </button>
          ))}
        </div>

        {/* CUISINE PACKAGE TAB */}
        {activeTab==='cuisine'&&(
          <div>
            <div style={{padding:'12px 16px',background:C.accentLight,borderRadius:10,marginBottom:16,fontSize:13,color:C.text2,border:`1px solid ${C.accent}33`}}>
              <strong>How it works:</strong> Select cuisines to filter → pick specific dishes → set package price per pax → add to proposal.
            </div>

            {/* Step 1: Filter by cuisine */}
            <div style={{marginBottom:14}}>
              {lbl('Step 1 — Filter by Cuisine (select multiple)')}
              <div style={{display:'flex',gap:6,flexWrap:'wrap'}}>
                {[...new Set(menuItems.map(m=>m.cuisine).filter(Boolean))].sort().map(cuisine=>{
                  const selected = selectedCuisines.includes(cuisine);
                  const count = menuItems.filter(m=>m.cuisine===cuisine).length;
                  return (
                    <button key={cuisine} onClick={()=>toggleCuisine(cuisine)}
                      style={{padding:'5px 12px',border:`2px solid ${selected?C.accent:C.border}`,borderRadius:8,
                        background:selected?C.accentLight:'transparent',color:selected?C.accent:C.text2,
                        fontSize:11,fontWeight:600,cursor:'pointer'}}>
                      {cuisine} <span style={{opacity:.6}}>({count})</span>
                    </button>
                  );
                })}
              </div>
            </div>

            {/* Step 2: Pick specific items */}
            {selectedCuisines.length>0&&(
              <div style={{marginBottom:14}}>
                {lbl(`Step 2 — Select Dishes (${selectedDishes.length} selected)`)}
                <div style={{maxHeight:240,overflowY:'auto',border:`1px solid ${C.border}`,borderRadius:10,overflowY:'auto'}}>
                  {Object.entries(
                    menuItems.filter(m=>selectedCuisines.includes(m.cuisine))
                      .filter((m,i,arr)=>arr.findIndex(x=>x.item_name===m.item_name&&x.cuisine===m.cuisine)===i)
                      .reduce((acc,m)=>{if(!acc[m.cuisine])acc[m.cuisine]=[];acc[m.cuisine].push(m);return acc;},{})
                  ).map(([cuisine,items])=>(
                    <div key={cuisine}>
                      <div style={{padding:'6px 14px',background:C.bg,fontSize:11,fontWeight:700,color:C.text3,textTransform:'uppercase',position:'sticky',top:0}}>
                        {cuisine}
                        <button onClick={()=>{
                          const ids = items.map(i=>i.id);
                          const allSelected = ids.every(id=>selectedDishes.find(d=>d.id===id));
                          if(allSelected) setSelectedDishes(p=>p.filter(d=>!ids.includes(d.id)));
                          else setSelectedDishes(p=>[...p.filter(d=>!ids.includes(d.id)),...items]);
                        }} style={{marginLeft:8,padding:'1px 8px',background:'transparent',border:`1px solid ${C.border2}`,borderRadius:4,fontSize:10,cursor:'pointer',color:C.text2}}>
                          {items.every(i=>selectedDishes.find(d=>d.id===i.id))?'Deselect All':'Select All'}
                        </button>
                      </div>
                      {items.map(item=>{
                        const isSelected = !!selectedDishes.find(d=>d.id===item.id);
                        return (
                          <div key={item.id} onClick={()=>setSelectedDishes(p=>isSelected?p.filter(d=>d.id!==item.id):[...p,item])}
                            style={{display:'flex',justifyContent:'space-between',alignItems:'center',padding:'8px 14px',
                              borderBottom:`1px solid ${C.border}`,cursor:'pointer',
                              background:isSelected?C.accentLight:'#fff',
                              borderLeft:isSelected?`3px solid ${C.accent}`:'3px solid transparent'}}>
                            <div style={{display:'flex',alignItems:'center',gap:8}}>
                              <input type="checkbox" checked={isSelected} onChange={()=>{}} style={{cursor:'pointer'}} />
                              <span style={{width:8,height:8,borderRadius:'50%',background:item.is_veg?C.accent:C.red,display:'inline-block',flexShrink:0}} />
                              <div>
                                <div style={{fontSize:13,fontWeight:isSelected?700:400,color:C.text}}>{item.item_name}</div>
                                {item.description&&<div style={{fontSize:10,color:C.text3}}>{item.description}</div>}
                              </div>
                            </div>
                            <div style={{fontSize:11,color:C.text3,flexShrink:0,marginLeft:8}}>{item.calories?`${item.calories} kcal`:''}</div>
                          </div>
                        );
                      })}
                    </div>
                  ))}
                </div>
              </div>
            )}

            {/* Step 3: Package details & price */}
            {selectedDishes.length>0&&(
              <div style={{marginBottom:14,padding:'14px 16px',background:C.bg,borderRadius:10,border:`1px solid ${C.border}`}}>
                {lbl('Step 3 — Package Name & Price')}
                <div style={{display:'grid',gridTemplateColumns:'1fr 1fr 1fr',gap:10,marginBottom:10}}>
                  <div>
                    {lbl('Meal Type')}
                    <select style={inp} value={pkgMealType} onChange={e=>setPkgMealType(e.target.value)}>
                      {['Welcome Drinks','Breakfast','Lunch','Hi-Tea','Snacks','Dinner','Cocktail Menu','Gala Dinner'].map(m=><option key={m}>{m}</option>)}
                    </select>
                  </div>
                  <div>
                    {lbl('Package Name (Optional)')}
                    <input style={inp} value={pkgName} onChange={e=>setPkgName(e.target.value)} placeholder="e.g. Royal Dinner Package" />
                  </div>
                  <div>
                    {lbl('Price Per Pax (Rs)')}
                    <input style={inp} type="number" value={pkgPricePerPax} onChange={e=>setPkgPricePerPax(e.target.value)} />
                  </div>
                </div>
                <div style={{fontSize:12,color:C.text3,marginBottom:10}}>
                  {selectedDishes.length} dishes selected · {pax} pax · Total: <strong style={{color:C.accent}}>{inr(pkgPricePerPax*pax)}</strong>
                </div>
                <div style={{display:'flex',flexWrap:'wrap',gap:4,marginBottom:10,maxHeight:80,overflowY:'auto'}}>
                  {selectedDishes.map(d=>(
                    <span key={d.id} style={{padding:'2px 8px',borderRadius:99,fontSize:10,background:C.surface,border:`1px solid ${C.border}`,color:C.text,display:'flex',alignItems:'center',gap:4}}>
                      <span style={{width:6,height:6,borderRadius:'50%',background:d.is_veg?C.accent:C.red,display:'inline-block'}} />
                      {d.item_name}
                      <span onClick={e=>{e.stopPropagation();setSelectedDishes(p=>p.filter(x=>x.id!==d.id));}} style={{cursor:'pointer',color:C.red,marginLeft:2,fontWeight:700}}>×</span>
                    </span>
                  ))}
                </div>
                <button onClick={()=>{
                  const name = pkgName || `${pkgMealType} — ${selectedCuisines.join(', ')}`;
                  const amt = parseFloat(pkgPricePerPax||0)*parseFloat(pax||0);
                  const newPkg = {
                    item_type:'package', item_name:name,
                    description:`${pkgMealType} · ${selectedDishes.length} dishes · ${selectedCuisines.join(', ')}`,
                    quantity:pax, unit:'pax', rate:parseFloat(pkgPricePerPax||0),
                    amount:amt, is_complimentary:false, sort_order:items.length,
                    cuisine_items:selectedDishes,
                  };
                  setItems(p=>[...p,newPkg]);
                  setSelectedDishes([]); setSelectedCuisines([]); setPkgName('');
                }}
                style={{padding:'10px 20px',background:C.accent,color:'#fff',border:'none',borderRadius:8,fontSize:13,fontWeight:700,cursor:'pointer'}}>
                  Add Package — {inr(pkgPricePerPax*pax)}
                </button>
              </div>
            )}

            {/* Existing packages */}
            {items.filter(i=>i.item_type==='package').length>0&&(
              <div style={{marginTop:14}}>
                <div style={{fontSize:12,fontWeight:700,color:C.text2,marginBottom:8}}>Added Packages:</div>
                {items.filter(i=>i.item_type==='package').map((item,i)=>{
                  const realIdx = items.indexOf(item);
                  return (
                    <div key={i} style={{padding:'12px 14px',background:C.accentLight,borderRadius:8,marginBottom:8,border:`1px solid ${C.accent}33`}}>
                      <div style={{display:'flex',justifyContent:'space-between',alignItems:'center',marginBottom:6}}>
                        <div>
                          <div style={{fontSize:13,fontWeight:700,color:C.text}}>{item.item_name}</div>
                          <div style={{fontSize:11,color:C.text3}}>{item.description}</div>
                        </div>
                        <div style={{display:'flex',alignItems:'center',gap:8}}>
                          <input type="number" value={item.rate} onChange={e=>updateItemRate(realIdx,e.target.value)}
                            style={{...inp,width:80,padding:'5px 8px'}} />
                          <span style={{fontSize:11,color:C.text3}}>/pax</span>
                          <div style={{fontWeight:800,color:C.accent}}>{inr(item.amount)}</div>
                          <button onClick={()=>removeItem(realIdx)} style={{background:C.redBg,border:'none',color:C.red,borderRadius:6,padding:'4px 8px',cursor:'pointer',fontSize:11}}>Remove</button>
                        </div>
                      </div>
                      {item.cuisine_items&&item.cuisine_items.length>0&&(
                        <div style={{display:'flex',flexWrap:'wrap',gap:3}}>
                          {item.cuisine_items.map((d,j)=>(
                            <span key={j} style={{padding:'2px 7px',borderRadius:99,fontSize:10,background:'#fff',border:`1px solid ${C.border}`,color:C.text,display:'flex',alignItems:'center',gap:3}}>
                              <span style={{width:5,height:5,borderRadius:'50%',background:d.is_veg?C.accent:C.red,display:'inline-block'}} />
                              {d.item_name}
                            </span>
                          ))}
                        </div>
                      )}
                    </div>
                  );
                })}
              </div>
            )}
          </div>
        )}
        {/* ADD-ONS TAB */}
        {activeTab==='addons'&&(
          <div>
            {/* Quick add suggestions */}
            <div style={{marginBottom:14}}>
              <div style={{fontSize:12,fontWeight:700,color:C.text2,marginBottom:8}}>Quick Add:</div>
              <div style={{display:'flex',gap:6,flexWrap:'wrap'}}>
                {ADDON_SUGGESTIONS.map(s=>(
                  <button key={s} onClick={()=>setAddonName(s)}
                    style={{padding:'5px 12px',border:`1px solid ${C.border}`,borderRadius:6,background:addonName===s?C.accentLight:C.surface,color:addonName===s?C.accent:C.text2,fontSize:12,cursor:'pointer'}}>
                    {s}
                  </button>
                ))}
              </div>
            </div>
            <div style={{display:'grid',gridTemplateColumns:'2fr 2fr 1fr',gap:10,marginBottom:10}}>
              <div>{lbl('Service Name')}<input style={inp} value={addonName} onChange={e=>setAddonName(e.target.value)} placeholder="e.g. DJ Setup" /></div>
              <div>{lbl('Description')}<input style={inp} value={addonDesc} onChange={e=>setAddonDesc(e.target.value)} placeholder="Details..." /></div>
              <div>{lbl('Amount (Rs)')}<input style={inp} type="number" value={addonAmt} onChange={e=>setAddonAmt(e.target.value)} disabled={addonFree} /></div>
            </div>
            <div style={{display:'flex',gap:16,alignItems:'center',marginBottom:12}}>
              <label style={{display:'flex',alignItems:'center',gap:6,cursor:'pointer',fontSize:13,color:C.text2,fontWeight:600}}>
                <input type="checkbox" checked={addonFree} onChange={e=>setAddonFree(e.target.checked)} />
                Complimentary
              </label>
              <button onClick={addAddon} disabled={!addonName}
                style={{padding:'8px 20px',background:C.accent,color:'#fff',border:'none',borderRadius:8,fontSize:13,fontWeight:700,cursor:'pointer',opacity:!addonName?.4:1}}>
                + Add
              </button>
            </div>

            {/* Added addons */}
            {items.filter(i=>i.item_type==='addon').length===0?(
              <div style={{textAlign:'center',padding:24,color:C.text3,border:`2px dashed ${C.border}`,borderRadius:10,fontSize:13}}>No add-ons yet. Use quick add buttons or type above.</div>
            ):(
              <div>
                {items.filter(i=>i.item_type==='addon').map((item,i)=>{
                  const realIdx = items.indexOf(item);
                  return (
                    <div key={i} style={{display:'flex',justifyContent:'space-between',alignItems:'center',padding:'10px 14px',background:C.amberBg,borderRadius:8,marginBottom:6,border:`1px solid ${C.amberBorder}`}}>
                      <div>
                        <div style={{fontSize:13,fontWeight:700,color:C.text}}>{item.item_name}</div>
                        {item.description&&<div style={{fontSize:11,color:C.text3}}>{item.description}</div>}
                      </div>
                      <div style={{display:'flex',alignItems:'center',gap:10}}>
                        {!item.is_complimentary&&(
                          <input type="number" value={item.rate} onChange={e=>updateItemRate(realIdx,e.target.value)}
                            style={{...inp,width:90,padding:'5px 8px'}} />
                        )}
                        <div style={{fontWeight:800,color:item.is_complimentary?C.accent:C.amber,minWidth:80,textAlign:'right'}}>
                          {item.is_complimentary?'Free':inr(item.amount)}
                        </div>
                        <button onClick={()=>removeItem(realIdx)} style={{background:C.redBg,border:'none',color:C.red,borderRadius:6,padding:'4px 8px',cursor:'pointer',fontSize:11}}>✕</button>
                      </div>
                    </div>
                  );
                })}
              </div>
            )}
          </div>
        )}

        {/* SUMMARY TAB */}
        {activeTab==='summary'&&(
          <div>
            {items.length===0?(
              <div style={{textAlign:'center',padding:40,color:C.text3}}>No items added yet</div>
            ):(
              <>
                {items.map((item,i)=>(
                  <div key={i} style={{display:'flex',justifyContent:'space-between',alignItems:'center',padding:'10px 14px',background:C.bg,borderRadius:8,marginBottom:6,border:`1px solid ${C.border}`}}>
                    <div style={{flex:1}}>
                      <div style={{display:'flex',alignItems:'center',gap:8}}>
                        <span style={{padding:'2px 8px',borderRadius:99,fontSize:10,fontWeight:700,background:item.item_type==='package'?C.accentLight:item.item_type==='addon'?C.amberBg:C.blueBg,color:item.item_type==='package'?C.accent:item.item_type==='addon'?C.amber:C.blue}}>{item.item_type}</span>
                        <span style={{fontSize:13,fontWeight:700,color:C.text}}>{item.item_name}</span>
                      </div>
                      <div style={{fontSize:11,color:C.text3,marginTop:2}}>{item.description}</div>
                    </div>
                    <div style={{display:'flex',alignItems:'center',gap:10}}>
                      <div style={{fontWeight:800,color:item.is_complimentary?C.accent:C.primary,fontSize:14}}>{item.is_complimentary?'Free':inr(item.amount)}</div>
                      <button onClick={()=>removeItem(i)} style={{background:C.redBg,border:'none',color:C.red,borderRadius:6,padding:'3px 8px',cursor:'pointer',fontSize:11}}>✕</button>
                    </div>
                  </div>
                ))}
                <div style={{padding:'12px 14px',background:C.accentLight,borderRadius:8,textAlign:'right',fontWeight:800,color:C.accent,fontSize:16,marginTop:8}}>
                  Function Total: {inr(fnTotal)}
                </div>
              </>
            )}
          </div>
        )}

        {/* Save */}
        <div style={{display:'flex',gap:10,marginTop:20,paddingTop:16,borderTop:`1px solid ${C.border}`}}>
          <button onClick={()=>onSave({function_name:name,function_date:date,function_time:time,venue,pax_count:pax,items})}
            disabled={!name}
            style={{flex:2,padding:'12px',background:C.accent,color:'#fff',border:'none',borderRadius:10,fontSize:14,fontWeight:700,cursor:'pointer',opacity:!name?.4:1}}>
            Save Function ({items.length} items · {inr(fnTotal)})
          </button>
          <button onClick={onClose} style={{flex:1,padding:'12px',background:'transparent',color:C.text2,border:`1px solid ${C.border}`,borderRadius:10,fontSize:14,cursor:'pointer'}}>Cancel</button>
        </div>
      </div>
    </div>
  );
}

// ── MAIN COMPONENT ──────────────────────────────────────────────────────────
export default function ProposalBuilder() {
  const [proposals, setProposals] = useState([]);
  const [menuItems, setMenuItems] = useState([]);
  const [loading, setLoading] = useState(true);
  const [view, setView] = useState('list');
  const [currentProposal, setCurrentProposal] = useState(null);
  const [editingFn, setEditingFn] = useState(null);
  const [showFnEditor, setShowFnEditor] = useState(false);
  const [msg, setMsg] = useState('');
  const [saving, setSaving] = useState(false);
  const [sending, setSending] = useState(false);
  const [form, setForm] = useState({
    client_name:'', client_phone:'', client_email:'',
    event_date:'', event_end_date:'', venue:'', pax_count:150,
    salesperson:'', valid_until:new Date(Date.now()+30*24*60*60*1000).toISOString().slice(0,10),
    discount_pct:0, discount_amount:0, gst_amount:0, advance_required:0,
    notes:'', terms:TERMS_DEFAULT,
  });
  const set = (k,v) => setForm(p=>({...p,[k]:v}));
  const showMsg = m => { setMsg(m); setTimeout(()=>setMsg(''),4000); };
  const currentUser = (() => { try { return JSON.parse(localStorage.getItem('botanix_user')); } catch { return null; } })();

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const [pRes, mRes] = await Promise.all([
        fetch(`${API}/proposals`).then(r=>r.json()),
        fetch(MENU_API).then(r=>r.json()),
      ]);
      setProposals(Array.isArray(pRes)?pRes:[]);
      setMenuItems(Array.isArray(mRes)?mRes.filter(m=>m.active_status):[]);
    } catch(e) {}
    setLoading(false);
  }, []);

  useEffect(()=>{ load(); },[load]);

  const loadProposal = async id => {
    const r = await fetch(`${API}/proposals/${id}`);
    const d = await r.json();
    setCurrentProposal(d);
    return d;
  };

  const proposalTotal = p => (p?.functions||[]).reduce((s,fn)=>(fn.items||[]).reduce((ss,i)=>ss+parseFloat(i.is_complimentary?0:i.amount||0),s),0);

  const saveProposal = async () => {
    if (!form.client_name) { showMsg('Client name required'); return; }
    setSaving(true);
    try {
      const r = await fetch(`${API}/proposals`, { method:'POST', headers:{'Content-Type':'application/json'}, body:JSON.stringify({...form, salesperson:form.salesperson||currentUser?.name}) });
      const d = await r.json();
      if (r.ok) { setCurrentProposal({...d,functions:[]}); setView('edit'); load(); showMsg('Proposal created! Now add functions.'); }
      else showMsg(d.error||'Failed');
    } catch(e) { showMsg('Error'); }
    setSaving(false);
  };

  const saveFn = async fnData => {
    if (!currentProposal) return;
    try {
      const url = editingFn?.id ? `${API}/proposals/${currentProposal.id}/functions/${editingFn.id}` : `${API}/proposals/${currentProposal.id}/functions`;
      const method = editingFn?.id ? 'PUT' : 'POST';
      const r = await fetch(url, { method, headers:{'Content-Type':'application/json'}, body:JSON.stringify(fnData) });
      if (r.ok) {
        setShowFnEditor(false); setEditingFn(null);
        const updated = await loadProposal(currentProposal.id);
        const total = proposalTotal(updated);
        await fetch(`${API}/proposals/${currentProposal.id}`, { method:'PUT', headers:{'Content-Type':'application/json'}, body:JSON.stringify({...updated, total_amount:total}) });
        showMsg('Function saved!');
      } else showMsg('Failed');
    } catch(e) { showMsg('Error'); }
  };

  const deleteFn = async fnId => {
    if (!window.confirm('Delete this function?')) return;
    await fetch(`${API}/proposals/${currentProposal.id}/functions/${fnId}`, {method:'DELETE'});
    loadProposal(currentProposal.id);
  };

  const sendProposal = async via => {
    setSending(true);
    try {
      await fetch(`${API}/proposals/${currentProposal.id}/send`, { method:'PUT', headers:{'Content-Type':'application/json'}, body:JSON.stringify({via}) });
      if (via==='whatsapp') {
        const text = `Dear ${currentProposal.client_name},\n\nThank you for considering Botanix Nature Resort!\n\nPlease find your exclusive wedding proposal:\nProposal #: ${currentProposal.proposal_number}\nTotal: ${inr(proposalTotal(currentProposal))}\nValid Until: ${currentProposal.valid_until?new Date(currentProposal.valid_until).toLocaleDateString('en-IN'):''}\n\nFor queries: reservations@botanix.in\n\nWarm regards,\n${currentProposal.salesperson||'Botanix Sales Team'}`;
        window.open(`https://wa.me/${(currentProposal.client_phone||'').replace(/[^0-9]/g,'')}?text=${encodeURIComponent(text)}`,'_blank');
      }
      if (via==='email') {
        window.location.href = `mailto:${currentProposal.client_email}?subject=Wedding Proposal ${currentProposal.proposal_number} - Botanix Nature Resort&body=Dear ${currentProposal.client_name},%0A%0APlease find your wedding proposal attached.%0A%0AProposal: ${currentProposal.proposal_number}%0ATotal: ${inr(proposalTotal(currentProposal))}%0A%0AWarm regards,%0A${currentProposal.salesperson||'Botanix Sales Team'}`;
      }
      if (via==='print') { setView('preview'); setTimeout(()=>window.print(),400); }
      load(); showMsg(`Sent via ${via}!`);
    } catch(e) { showMsg('Error'); }
    setSending(false);
  };

  const extendValidity = async days => {
    const d = new Date(currentProposal.valid_until||Date.now());
    d.setDate(d.getDate()+days);
    const newDate = d.toISOString().slice(0,10);
    await fetch(`${API}/proposals/${currentProposal.id}`, { method:'PUT', headers:{'Content-Type':'application/json'}, body:JSON.stringify({...currentProposal,valid_until:newDate}) });
    setCurrentProposal(p=>({...p,valid_until:newDate}));
    showMsg(`Extended to ${new Date(newDate).toLocaleDateString('en-IN')}`);
  };

  const fnTotal = fn => (fn.items||[]).reduce((s,i)=>s+parseFloat(i.is_complimentary?0:i.amount||0),0);
  const curTotal = proposalTotal(currentProposal);

  return (
    <div style={{padding:'24px 28px',background:C.bg,minHeight:'100vh'}}>
      {/* Header */}
      <div style={{display:'flex',justifyContent:'space-between',alignItems:'center',marginBottom:20}}>
        <div style={{display:'flex',alignItems:'center',gap:12}}>
          {view!=='list'&&<button onClick={()=>{setView('list');setCurrentProposal(null);}} style={{padding:'7px 14px',background:C.surface,border:`1px solid ${C.border}`,borderRadius:8,color:C.text2,fontSize:13,cursor:'pointer'}}>← Back</button>}
          <div>
            <div style={{fontSize:22,fontWeight:800,color:C.text}}>
              {view==='list'?'Wedding & Event Proposals':view==='create'?'New Proposal':view==='preview'?'Proposal Preview':`Editing — ${currentProposal?.proposal_number}`}
            </div>
            {view==='list'&&<div style={{fontSize:13,color:C.text3,marginTop:2}}>{proposals.length} proposals · Live menu from Kitchen Management</div>}
          </div>
        </div>
        <div style={{display:'flex',gap:8}}>
          {view==='list'&&<button onClick={()=>setView('create')} style={{padding:'9px 18px',background:C.accent,color:'#fff',border:'none',borderRadius:8,fontSize:13,fontWeight:700,cursor:'pointer'}}>+ New Proposal</button>}
          {view==='edit'&&currentProposal&&<button onClick={()=>setView('preview')} style={{padding:'9px 16px',background:BOTANIX_PRIMARY,color:'#fff',border:'none',borderRadius:8,fontSize:13,fontWeight:700,cursor:'pointer'}}>Preview & Send</button>}
        </div>
      </div>

      {msg&&<div style={{padding:'10px 16px',borderRadius:10,marginBottom:16,fontSize:13,fontWeight:600,background:C.accentLight,color:C.accent,border:`1px solid ${C.accent}`}}>{msg}</div>}

      {/* LIST */}
      {view==='list'&&(
        loading?<div style={{textAlign:'center',padding:60,color:C.text3}}>Loading...</div>:
        proposals.length===0?(
          <div style={{textAlign:'center',padding:60,background:C.surface,borderRadius:12,color:C.text3,border:`1px solid ${C.border}`}}>
            No proposals yet. Click "+ New Proposal" or use "Generate Proposal" from a wedding booking.
          </div>
        ):(
          <div style={{display:'grid',gridTemplateColumns:'repeat(auto-fill,minmax(300px,1fr))',gap:14}}>
            {proposals.map(p=>(
              <div key={p.id} style={{background:C.surface,border:`1px solid ${C.border}`,borderRadius:12,padding:20,borderTop:`4px solid ${BOTANIX_GOLD}`}}>
                <div style={{display:'flex',justifyContent:'space-between',marginBottom:10}}>
                  <div>
                    <div style={{fontSize:15,fontWeight:800,color:C.text}}>{p.client_name}</div>
                    <div style={{fontSize:11,color:C.text3}}>{p.proposal_number}</div>
                  </div>
                  <span style={{padding:'3px 10px',borderRadius:99,fontSize:11,fontWeight:700,background:p.status==='Sent'?C.accentLight:p.status==='Confirmed'?C.blueBg:C.amberBg,color:p.status==='Sent'?C.accent:p.status==='Confirmed'?C.blue:C.amber}}>{p.status}</span>
                </div>
                <div style={{fontSize:12,color:C.text2,lineHeight:1.9,marginBottom:12}}>
                  <div>📅 {p.event_date?new Date(p.event_date).toLocaleDateString('en-IN'):'—'}</div>
                  <div>👥 {p.pax_count} pax · {p.function_count||0} functions</div>
                  <div>👤 {p.salesperson||'—'}</div>
                  <div>⏳ Valid: {p.valid_until?new Date(p.valid_until).toLocaleDateString('en-IN'):'—'}</div>
                </div>
                <div style={{fontSize:20,fontWeight:800,color:BOTANIX_PRIMARY,marginBottom:12}}>{inr(p.total_amount)}</div>
                <div style={{display:'flex',gap:8}}>
                  <button onClick={async()=>{await loadProposal(p.id);setView('edit');}} style={{flex:1,padding:'7px',background:C.accentLight,color:C.accent,border:'none',borderRadius:8,fontSize:12,fontWeight:700,cursor:'pointer'}}>Edit</button>
                  <button onClick={async()=>{await loadProposal(p.id);setView('preview');}} style={{flex:1,padding:'7px',background:BOTANIX_PRIMARY,color:'#fff',border:'none',borderRadius:8,fontSize:12,fontWeight:700,cursor:'pointer'}}>Preview</button>
                </div>
              </div>
            ))}
          </div>
        )
      )}

      {/* CREATE */}
      {view==='create'&&(
        <div style={{maxWidth:640}}>
          <div style={{background:C.surface,border:`1px solid ${C.border}`,borderRadius:12,padding:24}}>
            <div style={{fontSize:15,fontWeight:800,color:C.text,marginBottom:16}}>Client & Event Details</div>
            <div style={{display:'grid',gridTemplateColumns:'1fr 1fr',gap:12}}>
              <div style={{gridColumn:'span 2'}}>{lbl('Client / Family Name *')}<input style={inp} value={form.client_name} onChange={e=>set('client_name',e.target.value)} placeholder="e.g. Sharma Family" /></div>
              <div>{lbl('Phone')}<input style={inp} value={form.client_phone} onChange={e=>set('client_phone',e.target.value)} /></div>
              <div>{lbl('Email')}<input style={inp} value={form.client_email} onChange={e=>set('client_email',e.target.value)} /></div>
              <div>{lbl('Event Start Date')}<input style={inp} type="date" value={form.event_date} onChange={e=>set('event_date',e.target.value)} /></div>
              <div>{lbl('Event End Date')}<input style={inp} type="date" value={form.event_end_date} onChange={e=>set('event_end_date',e.target.value)} /></div>
              <div>{lbl('Primary Venue')}<input style={inp} value={form.venue} onChange={e=>set('venue',e.target.value)} placeholder="Central Lawn" /></div>
              <div>{lbl('Expected Guests')}<input style={inp} type="number" value={form.pax_count} onChange={e=>set('pax_count',parseInt(e.target.value)||150)} /></div>
              <div>{lbl('Salesperson')}<input style={inp} value={form.salesperson} onChange={e=>set('salesperson',e.target.value)} /></div>
              <div>{lbl('Valid Until')}<input style={inp} type="date" value={form.valid_until} onChange={e=>set('valid_until',e.target.value)} /></div>
              <div>{lbl('Discount %')}<input style={inp} type="number" value={form.discount_pct} onChange={e=>set('discount_pct',e.target.value)} /></div>
              <div>{lbl('Advance Required (Rs)')}<input style={inp} type="number" value={form.advance_required} onChange={e=>set('advance_required',e.target.value)} /></div>
            </div>
            <div style={{marginTop:12}}>{lbl('Notes')}<textarea style={{...inp,height:60,resize:'vertical'}} value={form.notes} onChange={e=>set('notes',e.target.value)} /></div>
            <button onClick={saveProposal} disabled={saving} style={{width:'100%',padding:'13px',background:C.accent,color:'#fff',border:'none',borderRadius:10,fontSize:15,fontWeight:700,cursor:'pointer',marginTop:16,opacity:saving?.6:1}}>
              {saving?'Creating...':'Create Proposal & Add Functions →'}
            </button>
          </div>
        </div>
      )}

      {/* EDIT */}
      {view==='edit'&&currentProposal&&(
        <div>
          <div style={{display:'flex',gap:12,marginBottom:20,padding:'14px 18px',background:C.surface,borderRadius:12,border:`1px solid ${C.border}`,flexWrap:'wrap',alignItems:'center'}}>
            <div style={{flex:1}}>
              <div style={{fontSize:16,fontWeight:800,color:C.text}}>{currentProposal.client_name}</div>
              <div style={{fontSize:12,color:C.text3}}>{currentProposal.proposal_number} · {currentProposal.event_date?new Date(currentProposal.event_date).toLocaleDateString('en-IN'):''}</div>
            </div>
            <div style={{fontSize:22,fontWeight:800,color:BOTANIX_PRIMARY}}>{inr(curTotal)}</div>
            <div style={{display:'flex',gap:6}}>
              <button onClick={()=>extendValidity(7)} style={{padding:'5px 12px',background:C.accentLight,color:C.accent,border:'none',borderRadius:6,fontSize:12,fontWeight:600,cursor:'pointer'}}>+7 days</button>
              <button onClick={()=>extendValidity(30)} style={{padding:'5px 12px',background:C.accentLight,color:C.accent,border:'none',borderRadius:6,fontSize:12,fontWeight:600,cursor:'pointer'}}>+30 days</button>
            </div>
            <div style={{fontSize:12,color:C.text3}}>Valid: {currentProposal.valid_until?new Date(currentProposal.valid_until).toLocaleDateString('en-IN'):''}</div>
          </div>

          <div style={{display:'flex',justifyContent:'space-between',alignItems:'center',marginBottom:14}}>
            <div style={{fontSize:15,fontWeight:700,color:C.text}}>Functions ({(currentProposal.functions||[]).length})</div>
            <button onClick={()=>{setEditingFn(null);setShowFnEditor(true);}} style={{padding:'8px 16px',background:C.accent,color:'#fff',border:'none',borderRadius:8,fontSize:13,fontWeight:700,cursor:'pointer'}}>+ Add Function</button>
          </div>

          {(currentProposal.functions||[]).length===0?(
            <div style={{textAlign:'center',padding:40,background:C.surface,borderRadius:12,color:C.text3,border:`2px dashed ${C.border}`}}>
              No functions added. Click "+ Add Function" to add Mehendi, Sangeet, Wedding etc.
            </div>
          ):(
            <div style={{display:'grid',gap:12}}>
              {(currentProposal.functions||[]).map(fn=>{
                const fnDef = FUNCTIONS.find(f=>f.id===fn.function_name)||{label:fn.function_name,color:C.primary,icon:'🎉'};
                const pkgs = (fn.items||[]).filter(i=>i.item_type==='package');
                const addons = (fn.items||[]).filter(i=>i.item_type==='addon');
                return (
                  <div key={fn.id} style={{background:C.surface,border:`1px solid ${C.border}`,borderRadius:12,overflow:'hidden',borderLeft:`4px solid ${fnDef.color}`}}>
                    <div style={{display:'flex',justifyContent:'space-between',alignItems:'center',padding:'14px 18px',borderBottom:`1px solid ${C.border}`}}>
                      <div style={{display:'flex',alignItems:'center',gap:10}}>
                        <span style={{fontSize:22}}>{fnDef.icon}</span>
                        <div>
                          <div style={{fontSize:15,fontWeight:800,color:C.text}}>{fnDef.label}</div>
                          <div style={{fontSize:11,color:C.text3}}>
                            {fn.function_date&&new Date(fn.function_date).toLocaleDateString('en-IN')}
                            {fn.function_time&&` · ${fn.function_time}`}
                            {fn.venue&&` · ${fn.venue}`}
                            {` · ${fn.pax_count} pax`}
                          </div>
                        </div>
                      </div>
                      <div style={{display:'flex',alignItems:'center',gap:10}}>
                        <div style={{fontSize:16,fontWeight:800,color:fnDef.color}}>{inr(fnTotal(fn))}</div>
                        <button onClick={()=>{setEditingFn(fn);setShowFnEditor(true);}} style={{padding:'5px 12px',background:C.accentLight,color:C.accent,border:'none',borderRadius:6,fontSize:12,fontWeight:600,cursor:'pointer'}}>Edit</button>
                        <button onClick={()=>deleteFn(fn.id)} style={{padding:'5px 10px',background:C.redBg,color:C.red,border:'none',borderRadius:6,fontSize:12,cursor:'pointer'}}>Del</button>
                      </div>
                    </div>
                    <div style={{padding:'10px 18px',display:'flex',gap:6,flexWrap:'wrap'}}>
                      {pkgs.map((p,i)=><span key={i} style={{padding:'3px 10px',borderRadius:99,fontSize:11,fontWeight:600,background:C.accentLight,color:C.accent}}>{p.item_name} — {inr(p.amount)}</span>)}
                      {addons.map((a,i)=><span key={i} style={{padding:'3px 10px',borderRadius:99,fontSize:11,fontWeight:600,background:C.amberBg,color:C.amber}}>{a.item_name} {!a.is_complimentary&&`— ${inr(a.amount)}`}</span>)}
                      {(fn.items||[]).length===0&&<span style={{fontSize:11,color:C.text3}}>No items. Click Edit to add food packages & services.</span>}
                    </div>
                  </div>
                );
              })}
            </div>
          )}
        </div>
      )}

      {/* PREVIEW */}
      {view==='preview'&&currentProposal&&(
        <div>
          <div className="no-print" style={{display:'flex',gap:10,marginBottom:20,padding:'14px 18px',background:C.surface,borderRadius:12,border:`1px solid ${C.border}`,flexWrap:'wrap',alignItems:'center'}}>
            <div style={{flex:1,fontSize:14,fontWeight:700,color:C.text}}>{currentProposal.client_name} — {currentProposal.proposal_number} — {inr(proposalTotal(currentProposal))}</div>
            <button onClick={()=>sendProposal('whatsapp')} disabled={sending} style={{padding:'9px 16px',background:'#25D366',color:'#fff',border:'none',borderRadius:8,fontSize:13,fontWeight:700,cursor:'pointer'}}>📱 WhatsApp</button>
            <button onClick={()=>sendProposal('email')} disabled={sending} style={{padding:'9px 16px',background:C.blueBg,color:C.blue,border:`1px solid ${C.blue}`,borderRadius:8,fontSize:13,fontWeight:700,cursor:'pointer'}}>📧 Email</button>
            <button onClick={()=>{window.print();sendProposal('print');}} style={{padding:'9px 16px',background:BOTANIX_PRIMARY,color:'#fff',border:'none',borderRadius:8,fontSize:13,fontWeight:700,cursor:'pointer'}}>🖨️ Print / PDF</button>
            <button onClick={()=>setView('edit')} style={{padding:'9px 14px',background:C.bg,color:C.text2,border:`1px solid ${C.border}`,borderRadius:8,fontSize:13,cursor:'pointer'}}>← Edit</button>
          </div>
          <ProposalPrintView proposal={currentProposal} />
        </div>
      )}

      {showFnEditor&&<FunctionEditor fn={editingFn} menuItems={menuItems} onSave={saveFn} onClose={()=>{setShowFnEditor(false);setEditingFn(null);}} />}
    </div>
  );
}
